"use client";

import { ProductBadges } from "@/ui/components/catalog/product-badges";
import Image from "next/image";
import Link from "next/link";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import type { ProductCardData } from "@/ui/components/plp/product-card";

interface RelatedProductsCarouselProps {
	products: ProductCardData[];
	/** Pre-computed express range text per product id (e.g. "2–5 business days"). Null = no badge. */
	expressRangeTexts?: Map<string, string>;
}

export function RelatedProductsCarousel({ products, expressRangeTexts }: RelatedProductsCarouselProps) {
	return (
		<div className="scrollbar-thin flex gap-4 overflow-x-auto pb-4">
			{products.map((product) => {
				const pres = product.pricePresentation;
				const loc = product.locale;
				const ch = product.channel;
				const enriched = pres != null && loc != null && ch != null;
				const ploc = loc && isValidLocale(loc) ? (loc as Locale) : defaultLocale;
				const expressText = expressRangeTexts?.get(product.id) ?? null;

				return (
					<Link key={product.id} href={product.href} className="group flex w-40 shrink-0 flex-col sm:w-48">
						<div className="relative aspect-square overflow-hidden bg-secondary">
							<Image
								src={product.image}
								alt={product.imageAlt ?? product.name}
								width={192}
								height={192}
								className="group-hover:scale-105 h-full w-full object-cover transition-transform duration-300"
							/>
							{expressText && (
								<span
									role="status"
									aria-label={`Express from DE · ${expressText}`}
									className="bg-surface-primary-soft absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 border border-info-border px-1.5 py-0.5 text-[10px] font-medium text-info-foreground"
								>
									{/* DE flag */}
									<svg viewBox="0 0 5 3" className="h-2 w-3 rounded-[1px]" aria-hidden="true">
										<rect width="5" height="1" y="0" fill="#000000" />
										<rect width="5" height="1" y="1" fill="#DD0000" />
										<rect width="5" height="1" y="2" fill="#FFCE00" />
									</svg>
									<span>DE · {expressText}</span>
								</span>
							)}
						</div>
						<p className="group-hover:underline mt-2 line-clamp-2 text-sm font-medium">{product.name}</p>
						{product.brand && <p className="text-xs text-muted-foreground">{product.brand}</p>}
						<ProductBadges
							trustBadges={product.trustBadges}
							slug={product.slug}
							category={product.category}
						/>
						<div className="mt-1 flex items-baseline gap-2">
							<span className="text-sm font-medium">
								{enriched ? (
									<ReferencePrimaryPrice presentation={pres} locale={loc} channel={ch} size="sm" />
								) : (
									formatPrice(product.price, product.currency, ploc)
								)}
							</span>
							{product.compareAtPrice != null &&
								product.compareAtPrice > 0 &&
								(enriched && product.compareAtPresentation ? (
									<span className="text-xs text-muted-foreground line-through">
										<ReferencePrimaryPrice
											presentation={product.compareAtPresentation}
											locale={loc}
											channel={ch}
											size="sm"
											className="text-xs font-normal text-muted-foreground line-through"
										/>
									</span>
								) : (
									<span className="text-xs text-muted-foreground line-through">
										{formatPrice(product.compareAtPrice, product.currency, ploc)}
									</span>
								))}
						</div>
					</Link>
				);
			})}
		</div>
	);
}
